export { effectScope, isEffect, isEffectScope } from 'alien-signals';
import { createReactiveSystem, ReactiveFlags } from 'alien-signals/system';

var __defProp = Object.defineProperty;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __publicField = (obj, key, value) => __defNormalProp(obj, typeof key !== "symbol" ? key + "" : key, value);
var {
  link,
  unlink,
  propagate,
  checkDirty,
  shallowPropagate
} = createReactiveSystem({
  update(signal2) {
    return signal2.update();
  },
  notify(effect2) {
    queue.push(effect2);
  },
  unwatched() {
  }
});
var cycle = 0;
var batchDepth = 0;
var activeSub;
var queue = [];
var pauseStack = [];
function pauseTracking() {
  pauseStack.push(activeSub);
  activeSub = void 0;
}
function resumeTracking() {
  activeSub = pauseStack.pop();
}
var untracked = (fn) => {
  pauseTracking();
  try {
    return fn();
  } finally {
    resumeTracking();
  }
};
function signal(oldValue) {
  return new Signal(oldValue);
}
function computed(getter) {
  return new Computed(getter);
}
function effect(fn) {
  const e = new Effect(fn);
  e.run();
  return e;
}
function startBatch() {
  ++batchDepth;
}
function endBatch() {
  if (--batchDepth === 0) {
    flush();
  }
}
function batch(fn) {
  startBatch();
  try {
    return fn();
  } finally {
    endBatch();
  }
}
function flush() {
  while (queue.length > 0) {
    queue.shift().scheduler();
  }
}
function shouldUpdate(sub) {
  const flags = sub.flags;
  if (flags & ReactiveFlags.Dirty) {
    return true;
  }
  if (flags & ReactiveFlags.Pending) {
    if (checkDirty(sub.deps, sub)) {
      return true;
    }
    sub.flags = flags & ~ReactiveFlags.Pending;
  }
  return false;
}
var _a;
_a = "__s_isSignal" /* IS_SIGNAL */;
var Signal = class {
  constructor(value) {
    __publicField(this, _a, true);
    __publicField(this, "pendingValue");
    __publicField(this, "subs");
    __publicField(this, "subsTail");
    __publicField(this, "flags", ReactiveFlags.Mutable);
    __publicField(this, "currentValue");
    this.currentValue = this.pendingValue = value;
  }
  get() {
    if (shouldUpdate(this) && this.update()) {
      const subs = this.subs;
      if (subs !== void 0) {
        shallowPropagate(subs);
      }
    }
    if (activeSub !== void 0) {
      link(this, activeSub, cycle);
    }
    return this.pendingValue;
  }
  set(value) {
    this.currentValue = value;
    this.flags = ReactiveFlags.Mutable | ReactiveFlags.Dirty;
    const subs = this.subs;
    if (subs !== void 0) {
      propagate(subs);
      if (batchDepth === 0) {
        flush();
      }
    }
  }
  update() {
    this.flags = ReactiveFlags.Mutable;
    return this.pendingValue !== (this.pendingValue = this.currentValue);
  }
  get value() {
    return this.get();
  }
  set value(value) {
    this.set(value);
  }
  peek() {
    return this.currentValue;
  }
};
var _a2;
_a2 = "__s_isComputed" /* IS_COMPUTED */;
var Computed = class {
  constructor(getter) {
    this.getter = getter;
    __publicField(this, _a2, true);
    __publicField(this, "currentValue");
    __publicField(this, "subs");
    __publicField(this, "subsTail");
    __publicField(this, "deps");
    __publicField(this, "depsTail");
    __publicField(this, "flags", ReactiveFlags.Mutable | ReactiveFlags.Dirty);
  }
  get() {
    if (shouldUpdate(this) && this.update()) {
      const subs = this.subs;
      if (subs !== void 0) {
        shallowPropagate(subs);
      }
    }
    if (activeSub !== void 0) {
      link(this, activeSub, cycle);
    }
    return this.currentValue;
  }
  get value() {
    return this.get();
  }
  peek() {
    return untracked(this.getter);
  }
  update() {
    ++cycle;
    this.depsTail = void 0;
    this.flags = ReactiveFlags.Mutable | ReactiveFlags.RecursedCheck;
    const prevSub = activeSub;
    activeSub = this;
    try {
      return this.currentValue !== (this.currentValue = this.getter());
    } finally {
      activeSub = prevSub;
      this.flags &= ~ReactiveFlags.RecursedCheck;
      let toRemove = this.depsTail !== void 0 ? this.depsTail.nextDep : this.deps;
      while (toRemove !== void 0) {
        toRemove = unlink(toRemove, this);
      }
    }
  }
};
var Effect = class {
  constructor(fn) {
    this.fn = fn;
    __publicField(this, "deps");
    __publicField(this, "depsTail");
    __publicField(this, "flags", ReactiveFlags.Watching);
  }
  run() {
    ++cycle;
    this.depsTail = void 0;
    this.flags = ReactiveFlags.Watching | ReactiveFlags.RecursedCheck;
    const prevSub = activeSub;
    activeSub = this;
    try {
      return this.fn();
    } finally {
      activeSub = prevSub;
      this.flags &= ~ReactiveFlags.RecursedCheck;
      let toRemove = this.depsTail !== void 0 ? this.depsTail.nextDep : this.deps;
      while (toRemove !== void 0) {
        toRemove = unlink(toRemove, this);
      }
    }
  }
  scheduler() {
    if (this.shouldUpdate) {
      this.run();
    }
  }
  get shouldUpdate() {
    return shouldUpdate(this);
  }
  stop() {
    let dep = this.deps;
    while (dep !== void 0) {
      dep = unlink(dep, this);
    }
  }
  dirty() {
    return shouldUpdate(this);
  }
};

// src/deepSignal.ts
var proxyToSignals = /* @__PURE__ */ new WeakMap();
var objToProxy = /* @__PURE__ */ new WeakMap();
var arrayToArrayOfSignals = /* @__PURE__ */ new WeakMap();
var ignore = /* @__PURE__ */ new WeakSet();
var objToIterable = /* @__PURE__ */ new WeakMap();
var rg = /^\$/;
var descriptor = Object.getOwnPropertyDescriptor;
var peeking = false;
var isDeepSignal = (source) => {
  return proxyToSignals.has(source);
};
var isShallow = (source) => {
  return ignore.has(source);
};
var deepSignal = (obj) => {
  if (!shouldProxy(obj)) throw new Error("This object can't be observed.");
  if (!objToProxy.has(obj))
    objToProxy.set(obj, createProxy(obj, objectHandlers));
  return objToProxy.get(obj);
};
var peek = (obj, key) => {
  peeking = true;
  const value = obj[key];
  try {
    peeking = false;
  } catch (e) {
  }
  return value;
};
function shallow(obj) {
  ignore.add(obj);
  return obj;
}
var createProxy = (target, handlers) => {
  const proxy = new Proxy(target, handlers);
  ignore.add(proxy);
  return proxy;
};
var throwOnMutation = () => {
  throw new Error("Don't mutate the signals directly.");
};
var get = (isArrayOfSignals) => (target, fullKey, receiver) => {
  if (peeking) return Reflect.get(target, fullKey, receiver);
  let returnSignal = isArrayOfSignals || fullKey[0] === "$";
  if (!isArrayOfSignals && returnSignal && Array.isArray(target)) {
    if (fullKey === "$") {
      if (!arrayToArrayOfSignals.has(target))
        arrayToArrayOfSignals.set(target, createProxy(target, arrayHandlers));
      return arrayToArrayOfSignals.get(target);
    }
    returnSignal = fullKey === "$length";
  }
  if (!proxyToSignals.has(receiver)) proxyToSignals.set(receiver, /* @__PURE__ */ new Map());
  const signals = proxyToSignals.get(receiver);
  const key = returnSignal ? fullKey.replace(rg, "") : fullKey;
  if (!signals.has(key) && typeof descriptor(target, key)?.get === "function") {
    signals.set(
      key,
      computed(() => Reflect.get(target, key, receiver))
    );
  } else {
    let value = Reflect.get(target, key, receiver);
    if (returnSignal && typeof value === "function") return;
    if (typeof key === "symbol" && wellKnownSymbols.has(key)) return value;
    if (!signals.has(key)) {
      if (shouldProxy(value)) {
        if (!objToProxy.has(value))
          objToProxy.set(value, createProxy(value, objectHandlers));
        value = objToProxy.get(value);
      }
      signals.set(key, signal(value));
    }
  }
  return returnSignal ? signals.get(key) : signals.get(key).get();
};
var objectHandlers = {
  get: get(false),
  set(target, fullKey, val, receiver) {
    if (typeof descriptor(target, fullKey)?.set === "function")
      return Reflect.set(target, fullKey, val, receiver);
    if (!proxyToSignals.has(receiver)) proxyToSignals.set(receiver, /* @__PURE__ */ new Map());
    const signals = proxyToSignals.get(receiver);
    if (fullKey[0] === "$") {
      if (!(val instanceof Signal)) throwOnMutation();
      const key = fullKey.replace(rg, "");
      signals.set(key, val);
      return Reflect.set(target, key, val.peek(), receiver);
    } else {
      let internal = val;
      if (shouldProxy(val)) {
        if (!objToProxy.has(val))
          objToProxy.set(val, createProxy(val, objectHandlers));
        internal = objToProxy.get(val);
      }
      const isNew = !(fullKey in target);
      const result = Reflect.set(target, fullKey, val, receiver);
      if (!signals.has(fullKey)) signals.set(fullKey, signal(internal));
      else signals.get(fullKey).set(internal);
      if (isNew && objToIterable.has(target)) objToIterable.get(target).value++;
      if (Array.isArray(target) && signals.has("length"))
        signals.get("length").set(target.length);
      return result;
    }
  },
  deleteProperty(target, key) {
    if (key[0] === "$") throwOnMutation();
    const signals = proxyToSignals.get(objToProxy.get(target));
    const result = Reflect.deleteProperty(target, key);
    if (signals && signals.has(key)) signals.get(key).value = void 0;
    objToIterable.has(target) && objToIterable.get(target).value++;
    return result;
  },
  ownKeys(target) {
    if (!objToIterable.has(target)) objToIterable.set(target, signal(0));
    objToIterable._ = objToIterable.get(target).get();
    return Reflect.ownKeys(target);
  }
};
var arrayHandlers = {
  get: get(true),
  set: throwOnMutation,
  deleteProperty: throwOnMutation
};
var wellKnownSymbols = new Set(
  Object.getOwnPropertyNames(Symbol).map((key) => Symbol[key]).filter((value) => typeof value === "symbol")
);
var supported = /* @__PURE__ */ new Set([Object, Array]);
var shouldProxy = (val) => {
  if (typeof val !== "object" || val === null) return false;
  return supported.has(val.constructor) && !ignore.has(val);
};

// src/utils.ts
var objectToString = Object.prototype.toString;
var toTypeString = (value) => objectToString.call(value);
var isArray = Array.isArray;
var isMap = (val) => toTypeString(val) === "[object Map]";
var isSet = (val) => toTypeString(val) === "[object Set]";
var isFunction = (val) => typeof val === "function";
var isObject = (val) => val !== null && typeof val === "object";
var isPlainObject = (val) => toTypeString(val) === "[object Object]";
var hasChanged = (value, oldValue) => !Object.is(value, oldValue);
function NOOP() {
}
function isSignal(r) {
  return r ? r["__s_isSignal" /* IS_SIGNAL */] === true : false;
}
function isComputed(r) {
  return r ? r["__s_isComputed" /* IS_COMPUTED */] === true : false;
}
function unSignal(signal2) {
  return isSignal(signal2) || isComputed(signal2) ? signal2.value : signal2;
}
function toValue(source) {
  return isFunction(source) ? source() : unSignal(source);
}

// src/watch.ts
var INITIAL_WATCHER_VALUE = {};
var remove = (arr, el) => {
  const i = arr.indexOf(el);
  if (i > -1) {
    arr.splice(i, 1);
  }
};
function watch(source, cb, options = {}) {
  const { once, immediate, deep } = options;
  let effect2;
  let getter;
  let forceTrigger = false;
  let isMultiSource = false;
  const signalGetter = (source2) => {
    if (deep) return source2;
    if (isShallow(source2) || deep === false || deep === 0)
      return traverse(source2, 1);
    return traverse(source2);
  };
  const watchHandle = () => {
    effect2.stop();
    return effect2;
  };
  if (once && cb) {
    const _cb = cb;
    cb = (...args) => {
      _cb(...args);
      watchHandle();
    };
  }
  if (isSignal(source) || isComputed(source)) {
    getter = () => source.value;
    forceTrigger = isShallow(source);
  } else if (isDeepSignal(source)) {
    getter = () => signalGetter(source);
    forceTrigger = true;
  } else if (isArray(source)) {
    isMultiSource = true;
    forceTrigger = source.some((s) => isDeepSignal(s) || isShallow(s));
    getter = () => source.map((s) => {
      if (isSignal(s) || isComputed(s)) {
        return s.value;
      } else if (isDeepSignal(s)) {
        return signalGetter(s);
      }
    });
  } else if (isFunction(source)) {
    if (cb) {
      getter = source;
    } else {
      getter = () => {
        try {
          return source(effect2.stop);
        } finally {
        }
      };
    }
  } else {
    getter = NOOP;
    if (process.env.NODE_ENV !== "production") {
      console.warn(
        "Invalid watch source. Source must be a signal, a computed value !"
      );
    }
  }
  if (cb && deep) {
    const baseGetter = getter;
    const depth = deep === true ? Infinity : deep;
    getter = () => traverse(baseGetter(), depth);
  }
  let oldValue = isMultiSource ? new Array(source.length).fill(INITIAL_WATCHER_VALUE) : INITIAL_WATCHER_VALUE;
  const job = (immediateFirstRun) => {
    if (!immediateFirstRun && !effect2.shouldUpdate) {
      return;
    }
    if (cb) {
      const newValue = effect2.run();
      if (deep || forceTrigger || (isMultiSource ? newValue.some((v, i) => hasChanged(v, oldValue[i])) : hasChanged(newValue, oldValue))) {
        try {
          const args = [
            newValue,
            // pass undefined as the old value when it's changed for the first time
            oldValue === INITIAL_WATCHER_VALUE ? void 0 : isMultiSource && oldValue[0] === INITIAL_WATCHER_VALUE ? [] : oldValue,
            effect2.stop
          ];
          cb(...args);
          oldValue = newValue;
        } finally {
        }
      }
    } else {
      effect2.run();
    }
  };
  effect2 = new Effect(getter);
  effect2.scheduler = job;
  if (cb) {
    if (immediate) {
      job(true);
    } else {
      oldValue = effect2.run();
    }
  } else {
    effect2.run();
  }
  return watchHandle;
}
function traverse(value, depth = Infinity, seen) {
  if (depth <= 0 || !isObject(value) || value["__s_skip" /* SKIP */]) {
    return value;
  }
  seen = seen || /* @__PURE__ */ new Set();
  if (seen.has(value)) {
    return value;
  }
  seen.add(value);
  depth--;
  if (isSignal(value) || isComputed(value)) {
    traverse(value.value, depth, seen);
  } else if (isArray(value)) {
    for (let i = 0; i < value.length; i++) {
      traverse(value[i], depth, seen);
    }
  } else if (isSet(value) || isMap(value)) {
    value.forEach((v) => {
      traverse(v, depth, seen);
    });
  } else if (isPlainObject(value)) {
    for (const key in value) {
      traverse(value[key], depth, seen);
    }
    for (const key of Object.getOwnPropertySymbols(value)) {
      if (Object.prototype.propertyIsEnumerable.call(value, key)) {
        traverse(value[key], depth, seen);
      }
    }
  }
  return value;
}

// src/watchEffect.ts
function watchEffect(effect2) {
  return watch(effect2, void 0);
}

export { Computed, Effect, Signal, batch, computed, deepSignal, effect, endBatch, isArray, isComputed, isDeepSignal, isFunction, isMap, isObject, isPlainObject, isSet, isShallow, isSignal, pauseTracking, peek, remove, resumeTracking, shallow, signal, startBatch, toValue, traverse, unSignal, untracked, watch, watchEffect };
